import { createGlobalStyle } from 'styled-components';

export const Antd3GlobalStyle = createGlobalStyle`
    /* ── Global Font ─────────────────────────────────────────── */
    body,
    .ant-btn,
    .ant-input,
    .ant-select,
    .ant-table,
    .ant-tabs,
    .ant-form,
    .ant-modal,
    .ant-card,
    .ant-pagination,
    .ant-breadcrumb,
    .ant-notification-notice,
    .ant-message-notice-content,
    .ant-tooltip-inner,
    .ant-picker,
    .ant-spin-text,
    .ant-empty-description {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
    }

    /* ── Inputs ──────────────────────────────────────────────── */
    .ant-input,
    .ant-input-lg {
        border-radius: 8px !important;
        border-color: #dce1e8 !important;
        font-size: 14px !important;
        color: #3a4555 !important;
        transition: all 0.2s !important;
    }

    .ant-input:hover {
        border-color: #b8c2cf !important;
    }

    .ant-input:focus,
    .ant-input-focused {
        border-color: #1E7A45 !important;
        box-shadow: 0 0 0 3px rgba(30, 122, 69, 0.1) !important;
    }

    .ant-input::placeholder {
        color: #b0bac6 !important;
    }

    .ant-input-lg {
        height: 46px !important;
        padding: 10px 14px !important;
    }

    /* ── Selects ─────────────────────────────────────────────── */
    .ant-select-selector {
        border-radius: 8px !important;
        border-color: #dce1e8 !important;
        min-height: 40px !important;
    }

    .ant-select-lg .ant-select-selector {
        min-height: 46px !important;
    }

    .ant-select-focused .ant-select-selector {
        border-color: #1E7A45 !important;
        box-shadow: 0 0 0 3px rgba(30, 122, 69, 0.1) !important;
    }

    .ant-select-dropdown {
        border-radius: 10px !important;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
        padding: 6px !important;
        z-index: 1050 !important;
    }

    .ant-select-item {
        border-radius: 6px !important;
        padding: 8px 12px !important;
    }

    .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
        background: #E2F0E2 !important;
    }

    .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
        background: rgba(30, 122, 69, 0.08) !important;
        font-weight: 600 !important;
    }

    /* ── Buttons ─────────────────────────────────────────────── */
    .ant-btn {
        border-radius: 8px !important;
        height: 40px !important;
        padding: 4px 20px !important;
        font-weight: 500 !important;
        font-size: 14px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 6px !important;
    }

    .ant-btn-sm {
        height: 32px !important;
        padding: 0 12px !important;
        font-size: 13px !important;
        border-radius: 6px !important;
    }

    .ant-btn-lg {
        height: 48px !important;
        padding: 4px 28px !important;
        font-size: 15px !important;
    }

    .ant-btn-primary {
        background: #1E7A45 !important;
        border-color: #1E7A45 !important;
        box-shadow: none !important;
    }

    .ant-btn-primary,
    .ant-btn-primary span {
        color: #fff !important;
    }

    .ant-btn-primary:hover,
    .ant-btn-primary:focus {
        background: #1E7A45 !important;
        border-color: #1E7A45 !important;
        color: #fff !important;
        opacity: 0.85 !important;
        box-shadow: 0 2px 8px rgba(30, 122, 69, 0.2) !important;
    }

    .ant-btn-primary:hover span,
    .ant-btn-primary:focus span {
        color: #fff !important;
    }

    .ant-btn-default {
        background: #fff !important;
        border-color: #d9d3c2 !important;
        color: #3a4555 !important;
    }

    /* Default (white) buttons: opacity-only hover, no colour swap,
       matches the GoldButton "feedback without identity change" rule.
       Border colour uses warm-200 so the pill outline feels native to
       the warm-neutral palette rather than generic Antd grey. */
    .ant-btn-default:hover,
    .ant-btn-default:focus {
        background: #fff !important;
        border-color: #b8b0a0 !important;
        color: #3a4555 !important;
        opacity: 0.85 !important;
    }

    .ant-btn-default:hover span,
    .ant-btn-default:focus span {
        color: #3a4555 !important;
    }

    .ant-btn-dangerous,
    .ant-btn-danger {
        background: #fef2f2 !important;
        border-color: #fecaca !important;
        color: #dc2626 !important;
    }

    /* ── Tables ──────────────────────────────────────────────── */
    .ant-table-wrapper {
        border-radius: 10px !important;
        border: 1px solid #e8ecf2 !important;
        overflow: hidden !important;
    }

    /* Sticky table headers: the warm header strip stays pinned to the top
       while the rows scroll underneath. The z-index keeps it above row
       hover styles and the StatusPill chips. */
    .ant-table-thead > tr > th {
        position: sticky !important;
        top: 0 !important;
        z-index: 2 !important;
    }

    /* Table header — per design brief typography spec:
       Inter 11px 700 uppercase, letter-spacing 0.5px, warm-400 colour.
       Sits on warm-100 (#ede8da) with a thin warm-200 underline so it
       feels native to the warm-neutral palette. */
    .ant-table-thead > tr > th {
        background: #ede8da !important;
        color: #8b8275 !important;
        font-family: 'Inter', sans-serif !important;
        font-weight: 700 !important;
        font-size: 11px !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
        border-bottom: 1px solid #d9d3c2 !important;
        padding: 13px 20px !important;
    }

    .ant-table-thead > tr > th:hover {
        background: #f7f4ee !important;
        color: #6e6557 !important;
    }

    /* Sort indicator + active sort column take the forest accent */
    .ant-table-column-sort,
    .ant-table-column-sorter-up.active,
    .ant-table-column-sorter-down.active {
        color: #1e7a45 !important;
    }

    .ant-table-tbody > tr > td {
        padding: 14px 20px !important;
        border-bottom: 1px solid #f2f4f7 !important;
        color: #3a4555 !important;
        font-size: 14px !important;
    }

    .ant-table-tbody > tr:last-child > td {
        border-bottom: none !important;
    }

    .ant-table-tbody > tr:hover > td {
        background: #f8fafd !important;
    }

    .ant-table-tbody a {
        color: #1E7A45 !important;
        font-weight: 500 !important;
    }

    /* Pill-shaped row action buttons inside paginated tables */
    .ant-table-tbody .ant-btn {
        border-radius: 999px !important;
        padding-left: 14px !important;
        padding-right: 14px !important;
    }

    .ant-table-tbody .ant-btn-sm {
        padding-left: 12px !important;
        padding-right: 12px !important;
    }

    .ant-table-tbody a:hover {
        color: #176338 !important;
    }

    /* ── Pagination ──────────────────────────────────────────── */
    .ant-pagination-item {
        border-radius: 8px !important;
        border-color: #e0e4ea !important;
        min-width: 36px !important;
        height: 36px !important;
        line-height: 34px !important;
    }

    .ant-pagination-item-active {
        background: #1E7A45 !important;
        border-color: #1E7A45 !important;
    }

    .ant-pagination-item-active a {
        color: #fff !important;
    }

    .ant-pagination-prev .ant-pagination-item-link,
    .ant-pagination-next .ant-pagination-item-link {
        border-radius: 8px !important;
        border-color: #e0e4ea !important;
    }

    /* ── Tabs ────────────────────────────────────────────────── */
    .ant-tabs-nav::before {
        border-bottom: 1px solid #e8ecf2 !important;
    }

    .ant-tabs-tab {
        padding: 12px 2px !important;
        margin: 0 10px 0 0 !important;
        font-size: 13px !important;
        color: #8c95a6 !important;
        font-weight: 500 !important;
        white-space: nowrap !important;
    }

    @media only screen and (min-width: 576px) {
        .ant-tabs-tab {
            padding: 16px 4px !important;
            margin: 0 20px 0 0 !important;
            font-size: 14px !important;
        }
    }

    .ant-tabs-tab:hover {
        color: #3a4555 !important;
    }

    .ant-tabs-tab-active .ant-tabs-tab-btn {
        color: #1E7A45 !important;
        font-weight: 700 !important;
    }

    .ant-tabs-ink-bar {
        background: #1E7A45 !important;
        height: 3px !important;
        border-radius: 3px 3px 0 0 !important;
    }

    .ant-tabs-nav-wrap {
        justify-content: flex-start !important;
        overflow-x: auto !important;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;

        &::-webkit-scrollbar {
            display: none !important;
        }
    }

    @media only screen and (min-width: 743px) {
        .ant-tabs-nav-wrap {
            justify-content: center !important;
        }
    }

    .ant-tabs-nav-list {
        flex-wrap: nowrap !important;
    }

    .ant-tabs-nav-operations {
        display: none !important;
    }

    .ant-tabs-nav-wrap.ant-tabs-nav-wrap-ping-right::after,
    .ant-tabs-nav-wrap.ant-tabs-nav-wrap-ping-left::before {
        display: none !important;
    }

    /* ── Form Labels ─────────────────────────────────────────── */
    .ant-form-item-label > label,
    .ant-legacy-form-item-label label {
        font-weight: 600 !important;
        font-size: 13px !important;
        color: #3a4555 !important;
    }

    .ant-legacy-form-item {
        display: block !important;
        margin-bottom: 20px !important;
    }

    .ant-form-item {
        margin-bottom: 20px !important;
    }

    /* Input prefix positioning (legacy compat) */
    .ant-input-affix-wrapper {
        padding: 0 !important;
        border-radius: 8px !important;
        border-color: #dce1e8 !important;
    }

    .ant-input-affix-wrapper .ant-input-prefix {
        position: absolute !important;
        top: 50% !important;
        transform: translateY(-50%) !important;
        z-index: 2 !important;
        left: 12px !important;
        color: #b0bac6 !important;
    }

    .ant-input-affix-wrapper .ant-input {
        padding: 8px 12px !important;
        border: none !important;
        box-shadow: none !important;
    }

    .ant-input-affix-wrapper .ant-input:not(:first-child) {
        padding-left: 34px !important;
    }

    /* ── Modals ──────────────────────────────────────────────── */
    .ant-modal-mask {
        background: rgba(19, 43, 28, 0.5) !important;
        backdrop-filter: blur(3px) !important;
    }

    .ant-modal-content {
        border-radius: 14px !important;
        overflow: hidden !important;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
    }

    .ant-modal-header {
        padding: 20px 24px !important;
        background: #1e7a45 !important;
        border-bottom: none !important;
    }

    .ant-modal-title {
        font-weight: 700 !important;
        font-size: 16px !important;
        color: #fff !important;
        display: flex !important;
        align-items: center !important;
        gap: 10px !important;
    }

    .ant-modal-title .anticon {
        color: #a8c9a8 !important;
    }

    .ant-modal-body {
        padding: 24px !important;
    }

    .ant-modal-footer {
        padding: 16px 24px !important;
        border-top: 1px solid #edf0f4 !important;
    }

    .ant-modal-close-x {
        width: 56px !important;
        height: 56px !important;
        line-height: 56px !important;
        font-size: 16px !important;
        color: rgba(255, 255, 255, 0.65) !important;
        transition: color 0.15s !important;
    }

    .ant-modal-close:hover .ant-modal-close-x {
        color: #fff !important;
    }

    /* ── Cards ───────────────────────────────────────────────── */
    .ant-card {
        border-radius: 10px !important;
        border-color: #e8ecf2 !important;
    }

    .ant-card-head {
        border-bottom-color: #edf0f4 !important;
    }

    .ant-card-head-title {
        font-weight: 600 !important;
    }

    /* ── Notifications ───────────────────────────────────────── */
    .ant-notification-notice {
        border-radius: 10px !important;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
        padding: 16px 20px !important;
    }

    .ant-message-notice-content {
        border-radius: 10px !important;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1) !important;
    }

    /* ── Misc ────────────────────────────────────────────────── */
    .ant-tooltip-inner {
        border-radius: 8px !important;
    }

    .ant-popover-inner {
        border-radius: 10px !important;
    }

    .ant-divider {
        border-color: #edf0f4 !important;
    }

    .ant-spin-dot-item {
        background-color: #1E7A45 !important;
    }

    .ant-drawer-content-wrapper {
        box-shadow: -8px 0 24px rgba(0, 0, 0, 0.15) !important;
    }

    .ant-picker {
        border-radius: 8px !important;
        border-color: #dce1e8 !important;
    }

    .ant-picker:hover {
        border-color: #b8c2cf !important;
    }

    .ant-picker-focused {
        border-color: #1E7A45 !important;
        box-shadow: 0 0 0 3px rgba(30, 122, 69, 0.1) !important;
    }

    .ant-radio-wrapper,
    .ant-checkbox-wrapper {
        font-size: 14px !important;
    }

    .ant-legacy-form-item-control .ant-space-item {
        line-height: initial;
    }

    .ant-empty-description {
        color: #b0bac6 !important;
    }

    /* ── Notifications: fade in/out instead of slide; cap stack ─ */
    .ant-notification {
        z-index: 1100 !important;
    }

    .ant-notification-notice {
        border-radius: 12px !important;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12) !important;
        padding: 18px 44px 18px 22px !important;
        margin-bottom: 12px !important;
        border: 1px solid #edf0f4 !important;
    }

    /* Close × button: bigger hit target, visible cursor, hover affordance.
       Don't override position so Antd's own placement keeps working. */
    .ant-notification-notice-close {
        width: 28px !important;
        height: 28px !important;
        line-height: 28px !important;
        text-align: center !important;
        border-radius: 6px !important;
        color: #8c95a6 !important;
        cursor: pointer !important;
        transition: background 0.15s, color 0.15s !important;
    }

    .ant-notification-notice-close:hover {
        background: #f2f4f7 !important;
        color: #3a4555 !important;
    }

    .ant-notification-notice-close-x {
        line-height: 28px !important;
        font-size: 14px !important;
    }

    /* Pop-in (scale + fade) instead of horizontal slide. Only override the
       *enter* keyframes — leave Antd's leave animation alone so close works. */
    @keyframes cenefitsNotificationPop {
        0% {
            opacity: 0;
            transform: scale(0.92);
        }
        100% {
            opacity: 1;
            transform: scale(1);
        }
    }

    .ant-notification-fade-enter,
    .ant-notification-fade-appear {
        animation-name: cenefitsNotificationPop !important;
        animation-duration: 0.18s !important;
        animation-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1) !important;
    }

    /* ══════════════════════════════════════════════════════════
       Elevation & motion polish pass — Option B v4
       Tier 1: rest         — every surface
       Tier 2: hover/raised — interactive surfaces on hover
       Tier 3: focus ring   — focused interactive surfaces
       Bottle-tinted alphas so shadows feel native to the palette.
       ══════════════════════════════════════════════════════════ */

    .ant-card,
    .ant-modal-content,
    .ant-table-wrapper,
    .ant-popover-inner,
    .ant-dropdown-menu {
        box-shadow:
            0 1px 2px rgba(19, 43, 28, 0.04),
            0 1px 3px rgba(19, 43, 28, 0.06) !important;
        transition: box-shadow 0.18s cubic-bezier(0.4, 0, 0.2, 1),
            transform 0.18s cubic-bezier(0.4, 0, 0.2, 1) !important;
    }

    .ant-card:hover {
        box-shadow:
            0 4px 6px rgba(19, 43, 28, 0.05),
            0 10px 20px rgba(19, 43, 28, 0.08) !important;
        transform: translateY(-1px) !important;
    }

    /* Buttons & inputs: motion baseline + clearer focus ring */
    .ant-btn,
    .ant-input,
    .ant-input-affix-wrapper,
    .ant-select-selector,
    .ant-picker,
    .ant-tabs-tab {
        transition: background 0.15s cubic-bezier(0.4, 0, 0.2, 1),
            border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
            color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
            box-shadow 0.15s cubic-bezier(0.4, 0, 0.2, 1),
            transform 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important;
    }

    /* Non-primary, non-pill buttons get a subtle hover lift */
    .ant-btn:not(.ant-btn-link):not(.ant-btn-text):hover {
        transform: translateY(-1px) !important;
    }

    .ant-btn:not(.ant-btn-link):not(.ant-btn-text):active {
        transform: translateY(0) !important;
    }

    /* Table rows: forest accent rail on hover (sharp affordance) */
    .ant-table-tbody > tr {
        transition: background 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important;
        position: relative;
    }

    .ant-table-tbody > tr > td:first-child {
        position: relative;
    }

    .ant-table-tbody > tr:hover > td:first-child::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background: #1e7a45;
    }

    /* Focus ring for keyboard nav — a soft forest halo on any focusable
       element that doesn't already define one. Helps accessibility/WCAG. */
    .ant-btn:focus-visible,
    .ant-input:focus-visible,
    .ant-select-focused .ant-select-selector,
    .ant-checkbox-wrapper:focus-within,
    .ant-radio-wrapper:focus-within {
        outline: none !important;
        box-shadow: 0 0 0 3px rgba(30, 122, 69, 0.18) !important;
    }
`;
